import React, { useEffect, useRef, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChevronLeft,
  faChevronRight,
  faUsers,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { Table, Badge, Button, InputGroup, Form } from "@themesberg/react-bootstrap";

import { userMyReferralList } from "../../pages/api";
import EmptyState from "../dashboard/EmptyState";
import { ACTIVE_BUTTON_STYLE, buildReferralLink } from "../dashboard/dashboardHelpers";
import {
  PAGE_SIZE,
  formatCurrency,
  formatDate,
  mapDirectReferral,
  getPurchaseBadge,
  getLevelBadge,
} from "./networkHelpers";

function ReferralTableRow({ item, onViewDownline }) {
  const purchaseBadge = getPurchaseBadge(item.hasPurchase);
  const levelBadge = getLevelBadge(1);

  return (
    <tr>
      <td className="fw-semibold">{item.customUserId}</td>
      <td>{item.name}</td>
      <td>
        <div>{item.email}</div>
        {item.mobile !== "—" && <div className="small text-muted">{item.mobile}</div>}
      </td>
      <td>{formatDate(item.joinDate)}</td>
      <td>
        <Badge bg={purchaseBadge.bg} className="rounded-pill px-2 py-1 me-1">
          {purchaseBadge.label}
        </Badge>
        <Badge bg={levelBadge.bg} className="rounded-pill px-2 py-1">
          {levelBadge.label}
        </Badge>
        {item.courseName && <div className="small text-muted mt-1">{item.courseName}</div>}
      </td>
      <td className="text-end">{item.businessAmount > 0 ? formatCurrency(item.businessAmount) : "—"}</td>
      <td className="text-center">
        <Button size="sm" variant="outline-primary" onClick={() => onViewDownline?.(item.id, item.name)}>
          View Downline
        </Button>
      </td>
    </tr>
  );
}

function ReferralMobileCard({ item, onViewDownline }) {
  const purchaseBadge = getPurchaseBadge(item.hasPurchase);
  const levelBadge = getLevelBadge(1);

  return (
    <div className="network-user-card">
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div>
          <div className="network-user-card-title">{item.name}</div>
          <div className="network-user-card-meta">{item.customUserId}</div>
        </div>
        <Badge bg={levelBadge.bg}>{levelBadge.label}</Badge>
      </div>
      <div className="network-user-card-meta mb-1">{item.email}</div>
      <div className="network-user-card-meta mb-2">Joined: {formatDate(item.joinDate)}</div>
      <Badge bg={purchaseBadge.bg} className="me-1">{purchaseBadge.label}</Badge>
      {item.businessAmount > 0 && (
        <span className="small fw-semibold ms-1">{formatCurrency(item.businessAmount)}</span>
      )}
      <div className="mt-2">
        <Button size="sm" variant="outline-primary" className="w-100" onClick={() => onViewDownline?.(item.id, item.name)}>
          View Downline
        </Button>
      </div>
    </div>
  );
}

export default function DirectReferralsTab({
  onViewDownline,
  purchaseFilter = "",
  referralCode = "",
}) {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: PAGE_SIZE,
    total: 0,
  });

  const searchDebounceRef = useRef(null);
  const firstSearchRef = useRef(true);
  const referralLink = buildReferralLink(referralCode);

  const fetchReferrals = async (page = 1, searchTerm = "") => {
    try {
      setLoading(true);
      const response = await userMyReferralList({
        page,
        per_page: PAGE_SIZE,
        search: searchTerm,
      });
      const data = response?.data?.data || response?.data || {};
      let list = (Array.isArray(data?.referrals) ? data.referrals : []).map(mapDirectReferral);

      if (purchaseFilter === "purchased") list = list.filter((r) => r.hasPurchase);
      if (purchaseFilter === "not_purchased") list = list.filter((r) => !r.hasPurchase);

      setReferrals(list);
      setPagination({
        current_page: data?.pagination?.current_page ?? page,
        last_page: data?.pagination?.last_page ?? 1,
        per_page: data?.pagination?.per_page ?? PAGE_SIZE,
        total: data?.pagination?.total ?? list.length,
      });
    } catch {
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals(1, "");
  }, [purchaseFilter]);

  useEffect(() => {
    if (firstSearchRef.current) {
      firstSearchRef.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => fetchReferrals(1, search.trim()), 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search, purchaseFilter]);

  const getShowingRange = () => {
    const total = pagination.total;
    const current = pagination.current_page;
    const perPage = pagination.per_page;

    if (!total) return { start: 0, end: 0 };

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page;
    const current = pagination.current_page;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();

  return (
    <>
      <div className="network-filter-bar">
        <InputGroup style={{ maxWidth: 320, flex: "1 1 200px" }}>
          <InputGroup.Text className="bg-white">
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control placeholder="Search name, email..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </InputGroup>
      </div>

      {loading ? (
        <div className="text-center py-5 text-muted">Loading direct referrals...</div>
      ) : referrals.length === 0 ? (
        <div className="text-center">
          <EmptyState
            icon={faUsers}
            title="No referrals yet"
            message="Share your referral link to start building your network."
          />
          {referralLink && (
            <CopyToClipboard text={referralLink} onCopy={() => toast.success("Referral link copied")}>
              <Button style={ACTIVE_BUTTON_STYLE} size="sm" className="mt-2">
                <FontAwesomeIcon icon={faCopy} className="me-1" />
                Copy Referral Link
              </Button>
            </CopyToClipboard>
          )}
        </div>
      ) : (
        <>
          <div className="table-responsive network-table-wrap">
            <Table hover className="dashboard-table align-middle mb-0">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Name</th>
                  <th>Email / Mobile</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th className="text-end">Business</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((item) => (
                  <ReferralTableRow key={item.id} item={item} onViewDownline={onViewDownline} />
                ))}
              </tbody>
            </Table>
          </div>
          <div className="network-cards-wrap">
            {referrals.map((item) => (
              <ReferralMobileCard key={item.id} item={item} onViewDownline={onViewDownline} />
            ))}
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loading}
                className="course-page-btn"
                onClick={() =>
                  fetchReferrals(
                    pagination.current_page - 1,
                    search.trim()
                  )
                }
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {getPageItems().map((p, i) =>
                p === "..." ? (
                  <span key={i} className="px-2">
                    ...
                  </span>
                ) : (
                  <Button
                    key={p}
                    variant="outline-primary"
                    disabled={loading}
                    className={`course-page-btn ${p === pagination.current_page
                        ? "course-page-btn-active"
                        : ""
                      }`}
                    onClick={() =>
                      fetchReferrals(
                        p,
                        search.trim()
                      )
                    }
                  >
                    {p}
                  </Button>
                )
              )}

              <Button
                variant="outline-primary"
                disabled={
                  pagination.current_page >= pagination.last_page || loading
                }
                className="course-page-btn"
                onClick={() =>
                  fetchReferrals(
                    pagination.current_page + 1,
                    search.trim()
                  )
                }
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
